/**
 * Picking Workflow Simulator
 * Simulates the complete picking workflow using real API calls
 */

import { getSharedRateLimiter } from '../services/RateLimiter';
import { getSharedLockManager } from '../services/LockManager';
import { VirtualOperator } from '../services/VirtualOperator';
import { BatchSerialHelper } from '../services/BatchSerialHelper';
import { AuthService } from '../../../../../services/authService';
import { SimulatorConfig } from '../types';

// API base URL - use environment variable for production deployments
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Authenticated fetch helper using AuthService
const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  return AuthService.authenticatedFetch(url, options);
};

export interface PickingWorkflowResult {
  success: boolean;
  pListId?: number;
  linesProcessed: number;
  error?: string;
  duration: number;
}

export interface PListLineForSimulation {
  pListLineNo: number;
  itemNo: string;
  qty: number;
  qtyPicked: number;
  binCode: string;
}

export class PickingWorkflow {
  private config: SimulatorConfig;
  private operator: VirtualOperator;
  private rateLimiter = getSharedRateLimiter();
  private lockManager: ReturnType<typeof getSharedLockManager>;

  constructor(config: SimulatorConfig, operator: VirtualOperator) {
    this.config = config;
    this.operator = operator;
    this.lockManager = getSharedLockManager(config.companyNo);
  }

  /**
   * Execute one complete picking workflow cycle
   */
  async execute(): Promise<PickingWorkflowResult> {
    const startTime = Date.now();
    let pListId: number | undefined;
    let linesProcessed = 0;

    try {
      // Step 1: Get available picking lists (status 10 - Approved)
      this.operator.startTask('picking', 'Henter tilgjengelige plukklister');
      await this.rateLimiter.throttle();

      let availableLists = await this.getAvailablePickingLists();

      // If no picking lists, try auto-generation
      if (availableLists.length === 0) {
        const ruleGroupId = this.config.workflows.picking.ruleGroupId;
        const ordersToGenerate = this.config.workflows.picking.ordersToGenerate;

        if (!ruleGroupId || ruleGroupId === 0) {
          console.warn('[PickingWorkflow] Ingen plukklister og ingen regelgruppe konfigurert — kan ikke auto-generere. Sett regelgruppe i simulator-innstillinger.');
          return {
            success: true,
            linesProcessed: 0,
            duration: Date.now() - startTime,
            error: 'Ingen plukklister — regelgruppe ikke konfigurert'
          };
        }

        if (ordersToGenerate > 0) {
          this.operator.startTask('picking', `Genererer ${ordersToGenerate} salgsordrer...`);
          await this.rateLimiter.throttle();

          const generated = await this.generateOrdersAndPickLists();
          if (generated) {
            // Wait for RuleEngine to process, then fetch lists again
            await this.delay(2000);
            availableLists = await this.getAvailablePickingLists();
          }
        }
      }

      if (availableLists.length === 0) {
        console.log('[PickingWorkflow] Ingen tilgjengelige plukklister etter forsøk på auto-generering');
        return {
          success: true,
          linesProcessed: 0,
          duration: Date.now() - startTime,
          error: 'Ingen tilgjengelige plukklister'
        };
      }

      // Step 2: Select a random list and lock it
      const selectedList = availableLists[Math.floor(Math.random() * availableLists.length)];
      pListId = selectedList.pListID ?? selectedList.pListId ?? selectedList.PListId;

      if (pListId === undefined) {
        return {
          success: false,
          linesProcessed: 0,
          duration: Date.now() - startTime,
          error: 'Kunne ikke finne pListId i valgt liste'
        };
      }

      this.operator.startTask('picking', `Låser plukkliste ${pListId}`);
      await this.rateLimiter.throttle();

      const locked = await this.lockManager.acquireLock(pListId, this.operator.getId());
      if (!locked) {
        return {
          success: false,
          pListId,
          linesProcessed: 0,
          duration: Date.now() - startTime,
          error: 'Kunne ikke låse plukkliste'
        };
      }

      try {
        // Step 3: Start picking
        this.operator.startTask('picking', `Starter plukking av ${pListId}`);
        await this.rateLimiter.throttle();
        await this.startPicking(pListId);

        // Step 4: Get picking list details
        this.operator.startTask('picking', `Henter detaljer for ${pListId}`);
        await this.rateLimiter.throttle();
        const details = await this.getPickingDetails(pListId);

        // Step 5: Process each line
        const lines = details.lines || [];
        const batchSerialEnabled = this.config.batchSerialConfig?.enabled ?? false;
        const batchHelper = batchSerialEnabled ? new BatchSerialHelper() : null;

        for (const line of lines) {
          // Skip already fully picked lines
          if (line.qtyPicked >= line.qty) {
            continue;
          }

          this.operator.startTask('picking', `Plukker linje ${line.pListLineNo}`);

          // Simulate walking to location
          await this.delay(this.config.timing.walkTimeMs);

          // Simulate picking
          await this.delay(this.config.timing.pickTimeMs);

          // Batch/serial registration BEFORE updating picked quantity
          if (batchSerialEnabled && batchHelper) {
            const itemType = BatchSerialHelper.getItemTypeFromLine(line);
            const orderNo = line.orderNo ?? line.OrderNo ?? 0;
            const lineNo = line.lineNo ?? line.LineNo ?? line.pListLineNo;

            if (BatchSerialHelper.needsBatchTracking(itemType)) {
              // ItemType 4/5: Register batch numbers
              const batches = await batchHelper.getBatchesAtBin(
                line.itemNo, this.config.warehouseId, line.binCode
              );

              if (batches.length > 0) {
                // Pick from the first batch with sufficient quantity
                const selectedBatch = batches.find(b => b.quantity >= line.qty) || batches[0];
                const batchQty = Math.min(selectedBatch.quantity, line.qty);

                await this.rateLimiter.throttle();
                await this.registerBatchNumber(pListId, orderNo, lineNo, line.pListLineNo, selectedBatch.batchNo, batchQty);
                console.log(`[PickingWorkflow] Registered batch ${selectedBatch.batchNo} qty=${batchQty} for line ${line.pListLineNo}`);
              } else {
                console.warn(`[PickingWorkflow] No batches available for ${line.itemNo} at ${line.binCode}, skipping batch registration`);
              }
            } else if (BatchSerialHelper.needsSerialTracking(itemType)) {
              // ItemType 6: Register serial numbers (1 per unit)
              const serials = await batchHelper.getSerialNumbersAtBin(
                line.itemNo, this.config.warehouseId, line.binCode
              );

              const qtyToPick = Math.floor(line.qty - line.qtyPicked);
              const serialsToUse = serials.slice(0, qtyToPick);

              for (const serial of serialsToUse) {
                await this.rateLimiter.throttle();
                await this.registerSerialNumber(pListId, orderNo, lineNo, serial.serialNo);
                console.log(`[PickingWorkflow] Registered serial ${serial.serialNo} for line ${line.pListLineNo}`);
              }

              if (serialsToUse.length < qtyToPick) {
                console.warn(`[PickingWorkflow] Only ${serialsToUse.length}/${qtyToPick} serials available for ${line.itemNo} at ${line.binCode}`);
              }
            }
          }

          // Update picked quantity
          await this.rateLimiter.throttle();
          await this.updatePickedQuantity(pListId, line.pListLineNo, line.qty);

          linesProcessed++;
        }

        // Step 6: Complete picking
        this.operator.startTask('picking', `Fullfører plukking av ${pListId}`);
        await this.rateLimiter.throttle();
        await this.completePicking(pListId);

        // Step 7: Optionally send to packing
        if (this.config.workflows.packing.enabled && Math.random() > 0.5) {
          this.operator.startTask('picking', `Sender ${pListId} til pakking`);
          await this.rateLimiter.throttle();
          await this.sendToPacking(pListId);
        }

        return {
          success: true,
          pListId,
          linesProcessed,
          duration: Date.now() - startTime
        };

      } finally {
        // Always release the lock
        await this.lockManager.releaseLock(pListId, this.operator.getId());
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ukjent feil';
      console.error(`[PickingWorkflow] Error:`, errorMessage);

      return {
        success: false,
        pListId,
        linesProcessed,
        duration: Date.now() - startTime,
        error: errorMessage
      };
    }
  }

  /**
   * Get available picking lists
   * First try status 10 (Approved/Godkjent), then also check status 20 (Under plukking)
   * for lists that might have been unlocked but still in progress
   */
  private async getAvailablePickingLists(): Promise<any[]> {
    // Try the dedicated "available" endpoint first — filters by lock and operator
    try {
      const availableResponse = await authenticatedFetch(
        `${API_BASE_URL}/picking/available?warehouseId=${encodeURIComponent(this.config.warehouseId)}`
      );

      if (availableResponse.ok) {
        const availableLists = await availableResponse.json();
        if (Array.isArray(availableLists) && availableLists.length > 0) {
          // Filter to only status 10 (Approved) — ready for picking
          const approved = availableLists.filter((l: any) => {
            const status = l.pListStatus ?? l.PListStatus ?? l.plistStatus ?? 0;
            return status === 10;
          });
          if (approved.length > 0) {
            console.log(`[PickingWorkflow] Found ${approved.length} approved picking lists via /available`);
            return approved;
          }
        }
      }
    } catch (error) {
      console.warn('[PickingWorkflow] /picking/available failed, falling back to by-status:', error);
    }

    // Fallback: fetch by status 10 directly
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/by-status?status=10`
    );

    if (!response.ok) {
      console.warn(`[PickingWorkflow] Failed to fetch picking lists by status: ${response.statusText}`);
      return [];
    }

    const lists = await response.json();
    console.log(`[PickingWorkflow] Found ${Array.isArray(lists) ? lists.length : 0} picking lists with status 10`);
    return lists;
  }

  /**
   * Get picking list details
   */
  private async getPickingDetails(pListId: number): Promise<any> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/${pListId}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch picking details: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Start picking
   */
  private async startPicking(pListId: number): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/${pListId}/start`,
      {
        method: 'POST',
        body: JSON.stringify({ operatorId: this.operator.getId() })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to start picking: ${response.statusText}`);
    }
  }

  /**
   * Update picked quantity for a line
   */
  private async updatePickedQuantity(
    pListId: number,
    lineNo: number,
    qtyPicked: number
  ): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/${pListId}/lines`,
      {
        method: 'PUT',
        body: JSON.stringify({
          lineNo,
          qtyPicked,
          operatorId: this.operator.getId(),
          startDateTime: new Date().toISOString()
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to update picked quantity: ${response.statusText}`);
    }
  }

  /**
   * Complete picking
   */
  private async completePicking(pListId: number): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/${pListId}/complete`,
      {
        method: 'POST',
        body: JSON.stringify({ operatorId: this.operator.getId() })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to complete picking: ${response.statusText}`);
    }
  }

  /**
   * Send to packing
   */
  private async sendToPacking(pListId: number): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/${pListId}/to-packing`,
      {
        method: 'POST',
        body: JSON.stringify({ operatorId: this.operator.getId() })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to send to packing: ${response.statusText}`);
    }
  }

  /**
   * Register a batch number for a picking line
   * POST /api/v1/picking/lists/{pListId}/lines/{orderNo}/{lineNo}/batchnumbers
   */
  private async registerBatchNumber(
    pListId: number, orderNo: number, lineNo: number, pListLineNo: number, batchNo: string, quantity: number
  ): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/${pListId}/lines/${orderNo}/${lineNo}/batchnumbers`,
      {
        method: 'POST',
        body: JSON.stringify({
          pListID: pListId,
          orderNo,
          lineNo,
          pListLineNo,
          batchNo,
          quantity
        })
      }
    );

    if (!response.ok) {
      console.warn(`[PickingWorkflow] Failed to register batch number: ${response.statusText}`);
    }
  }

  /**
   * Register a serial number for a picking line
   * POST /api/v1/picking/lists/{pListId}/lines/{orderNo}/{lineNo}/serialnumbers
   */
  private async registerSerialNumber(
    pListId: number, orderNo: number, lineNo: number, serialNo: string
  ): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/picking/lists/${pListId}/lines/${orderNo}/${lineNo}/serialnumbers`,
      {
        method: 'POST',
        body: JSON.stringify({
          pListID: pListId,
          orderNo,
          lineNo,
          serialNo
        })
      }
    );

    if (!response.ok) {
      console.warn(`[PickingWorkflow] Failed to register serial number: ${response.statusText}`);
    }
  }

  /**
   * Generate orders and create picking lists using RuleGroupEngine
   */
  private async generateOrdersAndPickLists(): Promise<boolean> {
    try {
      const ruleGroupId = this.config.workflows.picking.ruleGroupId;

      // Check if rule group is configured
      if (!ruleGroupId || ruleGroupId === 0) {
        console.warn('[PickingWorkflow] Regelgruppe ikke konfigurert (ruleGroupId=0) — kan ikke opprette plukklister');
        return false;
      }
      console.log(`[PickingWorkflow] Bruker regelgruppe ${ruleGroupId} for plukkliste-opprettelse`);

      const ordersToGenerate = this.config.workflows.picking.ordersToGenerate;
      console.log(`[PickingWorkflow] Generating ${ordersToGenerate} orders...`);

      // Step 1: Generate random orders (API generates one order per call)
      const generatedOrderNos: string[] = [];

      for (let i = 0; i < ordersToGenerate; i++) {
        await this.rateLimiter.throttle();

        const orderResponse = await authenticatedFetch(
          `${API_BASE_URL}/orders/generate-random`,
          {
            method: 'POST',
            body: JSON.stringify({
              NumberOfLines: Math.floor(Math.random() * 3) + 2, // 2-4 lines per order
              AllowItemsWithoutStock: false,
              Warehouse: this.config.warehouseId,
              MaxQuantityPerLine: 10
            })
          }
        );

        if (orderResponse.ok) {
          const orderResult = await orderResponse.json();
          // API returns { order: { orderNo: "...", ... }, lines: [...], message: "..." }
          const orderNo = orderResult.order?.orderNo || orderResult.Order?.OrderNo;
          if (orderNo) {
            generatedOrderNos.push(orderNo);
          }
        } else {
          console.warn(`[PickingWorkflow] Failed to generate order ${i + 1}:`, orderResponse.statusText);
        }
      }

      if (generatedOrderNos.length === 0) {
        console.warn('[PickingWorkflow] No orders were generated');
        return false;
      }

      console.log(`[PickingWorkflow] Generated ${generatedOrderNos.length} orders: ${generatedOrderNos.join(', ')}. Executing rule group to create picking lists...`);

      // Step 2: Execute RuleGroupEngine to create picking lists from new orders
      await this.rateLimiter.throttle();

      // Generate a unique session ID for this simulation run
      const sessionId = `SIM-${this.operator.getId()}-${Date.now()}`;

      const ruleEngineResponse = await authenticatedFetch(
        `${API_BASE_URL}/${this.config.companyNo}/rulegroupengine/execute`,
        {
          method: 'POST',
          body: JSON.stringify({
            RuleGroupID: ruleGroupId,
            CompanyNo: this.config.companyNo,
            WarehouseID: this.config.warehouseId,
            SessionID: sessionId
          })
        }
      );

      if (!ruleEngineResponse.ok) {
        const errorText = await ruleEngineResponse.text().catch(() => ruleEngineResponse.statusText);
        console.warn('[PickingWorkflow] Failed to execute rule group:', errorText);
        return false;
      }

      const ruleResult = await ruleEngineResponse.json().catch(() => ({}));
      console.log('[PickingWorkflow] RuleGroupEngine executed successfully:', ruleResult);
      return true;

    } catch (error) {
      console.error('[PickingWorkflow] Error generating orders:', error);
      return false;
    }
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default PickingWorkflow;
